/**
 * Zustand auth store for Carnaf PWA. Persists access + refresh tokens in
 * localStorage under carnaf_* keys (mirrors the dashboard fs_* convention
 * but Carnaf-namespaced so we don't collide with the building tenant flow
 * on shared devices).
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface CarnafSession {
  userId: string;
  tenantId: string;
  email: string | null;
  phone: string | null;
  name: string | null;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: CarnafSession | null;
  setSession: (data: { accessToken: string; refreshToken: string; user: CarnafSession }) => void;
  /** Refresh the display name in-place (e.g. after the user renames themselves). */
  updateName: (name: string | null) => void;
  clear: () => void;
  isAuthenticated: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      setSession: ({ accessToken, refreshToken, user }) => set({ accessToken, refreshToken, user }),
      updateName: (name) => set((s) => (s.user ? { user: { ...s.user, name } } : {})),
      clear: () => set({ accessToken: null, refreshToken: null, user: null }),
      isAuthenticated: () => Boolean(get().accessToken),
    }),
    {
      name: 'carnaf-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        accessToken: s.accessToken,
        refreshToken: s.refreshToken,
        user: s.user,
      }),
    },
  ),
);
